import {
  cashbookImportCommitSchema,
  cashbookImportDryRunSchema,
  findCashbookCategory,
  findCashbookImportDuplicates,
  isCashbookOpeningConfigured,
  isDebtPaymentCategory,
  isLoanReceiptCategory
} from '@finvox/shared/cashbook';
import { roundMoney, toMoney } from '@finvox/shared/money';
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { getFirestoreDb } from '@/lib/firebase';
import {
  assertEntryDateInPeriod,
  assertOnOrAfterOpening,
  assertPeriodOpen,
  bumpSyncVersion,
  cashbookEntriesCol,
  invalidateCashbookContextCache,
  loadActiveEntriesForDuplicateCheck,
  loadCashbookSettings
} from './context';
import type { CashbookPermissions } from './mutations';

export type ImportDryRunResult = {
  ok: boolean;
  errors: Array<{ index: number; message: string }>;
  warnings: Array<{ index: number; message: string }>;
  rowCount: number;
};

function assertCanImport(permissions: CashbookPermissions): void {
  if (!permissions.import) throw new Error('Insufficient permissions');
}

export async function dryRunCashbookImport(
  orgId: string,
  input: unknown,
  permissions: CashbookPermissions
): Promise<ImportDryRunResult> {
  assertCanImport(permissions);
  const data = cashbookImportDryRunSchema.parse(input);
  const db = getFirestoreDb();
  await assertPeriodOpen(db, orgId, data.periodId);

  const settings = await loadCashbookSettings(db, orgId);
  if (!isCashbookOpeningConfigured(settings)) throw new Error('Set opening treasury first');

  const errors: Array<{ index: number; message: string }> = [];
  const warnings: Array<{ index: number; message: string }> = [];
  const existingActive = await loadActiveEntriesForDuplicateCheck(db, orgId);
  const dupes = findCashbookImportDuplicates(data.rows, existingActive);

  for (const idx of dupes.inFileDuplicateIndices) {
    errors.push({ index: idx, message: 'Duplicate row in file' });
  }
  for (const idx of dupes.existingMatchIndices) {
    warnings.push({ index: idx, message: 'Matches an existing entry' });
  }

  for (let i = 0; i < data.rows.length; i++) {
    const row = data.rows[i]!;
    if (isDebtPaymentCategory(row.categoryKey)) {
      errors.push({ index: i, message: 'Use Pay debt to record debt payments' });
    }
    if (isLoanReceiptCategory(row.categoryKey)) {
      errors.push({
        index: i,
        message: 'Use Add debt with Borrowed money to record loan cash'
      });
    }
    const cat = findCashbookCategory(row.categoryKey, settings.categories ?? []);
    if (!cat || cat.type !== row.type) {
      errors.push({ index: i, message: 'Invalid category' });
    }
    if (toMoney(row.amount).lte(0)) {
      errors.push({ index: i, message: 'Amount must be positive' });
    }
    try {
      assertOnOrAfterOpening(settings, row.date);
      await assertEntryDateInPeriod(db, orgId, data.periodId, row.date);
    } catch (e) {
      errors.push({
        index: i,
        message: e instanceof Error ? e.message : 'Invalid date'
      });
    }
  }

  return {
    ok: errors.length === 0,
    errors,
    warnings,
    rowCount: data.rows.length
  };
}

export async function commitCashbookImport(
  orgId: string,
  uid: string,
  input: unknown,
  permissions: CashbookPermissions
): Promise<{ ok: true; imported: number }> {
  const dry = await dryRunCashbookImport(orgId, input, permissions);
  if (!dry.ok) throw new Error('Import validation failed');

  const data = cashbookImportCommitSchema.parse(input);
  const db = getFirestoreDb();

  await runTransaction(db, async (tx) => {
    for (const row of data.rows) {
      const entryRef = doc(cashbookEntriesCol(db, orgId));
      const memo = row.memo?.trim() || undefined;
      tx.set(entryRef, {
        type: row.type,
        amount: roundMoney(row.amount),
        date: row.date,
        categoryKey: row.categoryKey,
        ...(memo ? { memo } : {}),
        ...(row.type === 'out'
          ? {
              fundingSource:
                row.fundingSource === 'drawer' || row.fundingSource === 'treasury'
                  ? row.fundingSource
                  : 'unclassified'
            }
          : {}),
        periodId: data.periodId,
        status: 'active',
        createdBy: uid,
        createdAt: serverTimestamp(),
        imported: true
      });
    }
    bumpSyncVersion(tx, db, orgId);
  });

  invalidateCashbookContextCache(orgId);
  return { ok: true, imported: data.rows.length };
}
